/* ==========================================================================
   rx-contact.js — rx-contact behaviours. Plain JS.
   - <rx-store-hours>  open / closed status for settings.rx_timezone (Intl),
                       hours dropdown (WP scripts/rolex/contact.js + the
                       server-side status in flex/map.blade.php)
   - <rx-map>          Google Maps JS map, styled as WP google-maps.blade.php
                       (global window.initMap callback, any number of maps),
                       loaded only when it scrolls into view; hidden when the
                       key is refused (window.gm_authFailure) or fails to load
   - <rx-contact-form> the 2-step Rolex contact form (WP Gravity Forms 6 / 7):
                       Next checks the message, Previous goes back, submit
                       checks the required fields; after a successful post
                       (Shopify reloads with contact_posted=true) it sends
                       _satellite.track('contactForm') once Launch is ready.
                       Form relay (data-relay-url, Theme settings → Rolex):
                       posts form_id rolex-contact as JSON instead; 200 → same confirmation + contactForm
                       once; 422 → messages under the fields; 429 / 4xx →
                       message; network / 15 s timeout / 5xx → native submit
                       (not without an email: error message instead).
                       Email OR phone required (relay on or off).
   Status texts are WP's: "Currently Open", "Currently Closed", "Closed today".
   ========================================================================== */
(function () {
  'use strict';

  var DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  var RANGE = /^\s*(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})\s*$/;

  function to12h(h, m) {
    var suffix = h >= 12 ? 'PM' : 'AM';
    var hour = h % 12 === 0 ? 12 : h % 12;
    return hour + ':' + (m < 10 ? '0' : '') + m + ' ' + suffix;
  }

  function formatValue(value) {
    var match = RANGE.exec(value || '');
    if (!match) return value;
    return to12h(+match[1], +match[2]) + ' – ' + to12h(+match[3], +match[4]);
  }

  function nowIn(timeZone) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone || undefined,
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(new Date());
    } catch (e) {
      parts = new Intl.DateTimeFormat('en-US', {
        weekday: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date());
    }
    var out = {};
    parts.forEach(function (p) { out[p.type] = p.value; });
    return {
      day: String(out.weekday || '').toLowerCase(),
      minutes: (parseInt(out.hour, 10) % 24) * 60 + parseInt(out.minute, 10)
    };
  }

  class RxStoreHours extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-rx-hours]');
      try {
        this.hours = JSON.parse(data ? data.textContent : '{}');
      } catch (e) {
        this.hours = {};
      }
      this.toggle = this.querySelector('[data-rx-hours-toggle]');
      this.list = this.toggle ? document.getElementById(this.toggle.getAttribute('aria-controls')) : null;
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
      this.addEventListener('keydown', this.onKeydown);

      Array.prototype.forEach.call(this.querySelectorAll('[data-rx-hours-value]'), function (el) {
        el.textContent = formatValue(el.textContent.trim());
      });
      this.render();
      this.timer = window.setInterval(this.render.bind(this), 60000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    render() {
      var now = nowIn(this.getAttribute('data-timezone'));
      if (DAYS.indexOf(now.day) === -1) return;
      var today = (this.hours[now.day] || '').trim();
      var status;
      var match = RANGE.exec(today);
      if (!today || /closed/i.test(today)) {
        status = 'Closed today';
      } else if (match) {
        var open = (+match[1]) * 60 + (+match[2]);
        var close = (+match[3]) * 60 + (+match[4]);
        status = now.minutes >= open && now.minutes <= close ? 'Currently Open' : 'Currently Closed';
      } else {
        status = '';
      }
      var statusEl = this.querySelector('[data-rx-hours-status]');
      var todayEl = this.querySelector('[data-rx-hours-today]');
      if (statusEl) statusEl.textContent = status;
      if (todayEl) todayEl.textContent = match ? formatValue(today) : '';
    }

    onToggle() {
      if (!this.list) return;
      var open = this.toggle.getAttribute('aria-expanded') !== 'true';
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.list.hidden = !open;
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.toggle && this.toggle.getAttribute('aria-expanded') === 'true') {
        this.onToggle();
        this.toggle.focus();
      }
    }
  }

  /* Google Maps --------------------------------------------------------------- */
  var MAP_STYLES = [{"featureType":"administrative","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"administrative","elementType":"labels","stylers":[{"visibility":"on"}]},{"featureType":"administrative","elementType":"labels.text.fill","stylers":[{"color":"#6e6c6c"}]},{"featureType":"administrative.country","elementType":"geometry","stylers":[{"weight":"0.85"},{"lightness":"45"},{"visibility":"on"}]},{"featureType":"landscape","elementType":"all","stylers":[{"color":"#f2f2f2"}]},{"featureType":"landscape","elementType":"geometry","stylers":[{"gamma":"1.09"},{"lightness":"19"}]},{"featureType":"landscape","elementType":"labels","stylers":[{"visibility":"on"},{"lightness":"-21"},{"weight":"0.01"}]},{"featureType":"landscape.natural.landcover","elementType":"geometry","stylers":[{"saturation":"8"},{"lightness":"8"}]},{"featureType":"landscape.natural.landcover","elementType":"geometry.fill","stylers":[{"lightness":"6"}]},{"featureType":"poi","elementType":"all","stylers":[{"visibility":"off"},{"saturation":"1"},{"color":"#cfe1d3"}]},{"featureType":"poi","elementType":"geometry","stylers":[{"visibility":"off"}]},{"featureType":"poi.attraction","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"poi.medical","elementType":"geometry.fill","stylers":[{"visibility":"off"}]},{"featureType":"poi.medical","elementType":"geometry.stroke","stylers":[{"visibility":"on"}]},{"featureType":"poi.park","elementType":"geometry","stylers":[{"visibility":"on"},{"color":"#cfe1d3"}]},{"featureType":"road","elementType":"all","stylers":[{"saturation":-100},{"lightness":45}]},{"featureType":"road","elementType":"geometry.fill","stylers":[{"visibility":"on"}]},{"featureType":"road.highway","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"road.highway","elementType":"geometry","stylers":[{"visibility":"on"},{"color":"#dcdfe2"}]},{"featureType":"road.highway","elementType":"geometry.fill","stylers":[{"visibility":"on"}]},{"featureType":"road.highway","elementType":"geometry.stroke","stylers":[{"visibility":"off"}]},{"featureType":"road.highway","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"road.highway.controlled_access","elementType":"geometry","stylers":[{"visibility":"off"}]},{"featureType":"road.highway.controlled_access","elementType":"geometry.stroke","stylers":[{"visibility":"on"}]},{"featureType":"road.highway.controlled_access","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"road.arterial","elementType":"geometry","stylers":[{"visibility":"on"}]},{"featureType":"road.arterial","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"road.local","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"road.local","elementType":"geometry","stylers":[{"visibility":"on"}]},{"featureType":"road.local","elementType":"geometry.fill","stylers":[{"visibility":"on"}]},{"featureType":"road.local","elementType":"labels","stylers":[{"visibility":"on"}]},{"featureType":"transit","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"geometry.fill","stylers":[{"visibility":"on"}]},{"featureType":"transit.line","elementType":"geometry","stylers":[{"visibility":"off"}]},{"featureType":"transit.station","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"transit.station","elementType":"geometry","stylers":[{"gamma":"1.57"},{"lightness":"-10"}]},{"featureType":"transit.station","elementType":"labels","stylers":[{"weight":"0.01"},{"visibility":"on"},{"saturation":"-100"},{"lightness":"-3"}]},{"featureType":"transit.station","elementType":"labels.icon","stylers":[{"weight":"0.01"}]},{"featureType":"water","elementType":"geometry","stylers":[{"color":"#c2d8e1"}]},{"featureType":"water","elementType":"labels.text","stylers":[{"visibility":"on"},{"color":"#b0bdc7"}]}];
  // WP google-maps.blade.php: one global initMap is the Maps script's callback.
  // Every <rx-map> on the page queues itself; the script is added once (with
  // the first map's key) when the first map nears the viewport.
  var mapsState = 'idle';                 // idle | loading | ready | failed
  var pendingMaps = [];

  function mapsReady() {
    return !!(window.google && window.google.maps && window.google.maps.Map);
  }

  function mapsFailed() {
    mapsState = 'failed';
    pendingMaps = [];
    Array.prototype.forEach.call(document.querySelectorAll('rx-map'), function (el) {
      if (typeof el.fail === 'function') el.fail();
    });
  }

  // Keep any initMap / gm_authFailure another script already set, and call it too.
  var otherInitMap = typeof window.initMap === 'function' ? window.initMap : null;
  window.initMap = function () {
    mapsState = 'ready';
    var queue = pendingMaps;
    pendingMaps = [];
    queue.forEach(function (el) { el.draw(); });
    if (otherInitMap) otherInitMap.apply(this, arguments);
  };

  // Google calls this when the key is refused (e.g. RefererNotAllowedMapError).
  var otherAuthFailure = typeof window.gm_authFailure === 'function' ? window.gm_authFailure : null;
  window.gm_authFailure = function () {
    mapsFailed();
    if (otherAuthFailure) otherAuthFailure.apply(this, arguments);
  };

  function requestMap(el) {
    if (mapsState === 'failed') return el.fail();
    if (mapsState === 'ready' || mapsReady()) {
      mapsState = 'ready';
      return el.draw();
    }
    pendingMaps.push(el);
    if (mapsState === 'loading') return;
    mapsState = 'loading';
    var script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(el.getAttribute('data-api-key')) + '&callback=initMap&loading=async';
    script.async = true;
    script.defer = true;
    script.onerror = mapsFailed;
    document.head.appendChild(script);
  }

  class RxMap extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('[data-rx-map-canvas]');
      this.position = this.coords('data-lat', 'data-lng');
      if (!this.canvas || !this.position || !this.getAttribute('data-api-key')) return this.fail();
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) {
            this.observer.disconnect();
            requestMap(this);
          }
        }.bind(this), { rootMargin: '200px' });
        this.observer.observe(this);
      } else {
        requestMap(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    coords(latAttr, lngAttr) {
      var lat = parseFloat(this.getAttribute(latAttr));
      var lng = parseFloat(this.getAttribute(lngAttr));
      return isNaN(lat) || isNaN(lng) ? null : { lat: lat, lng: lng };
    }

    draw() {
      if (this.drawn || this.failed) return;
      this.drawn = true;
      var gm = google.maps;
      // cameraControl (move + zoom) exists from Maps JS 3.58; older versions get zoomControl.
      var version = String(gm.version || '').split('.');
      var hasCamera = Number(version[0]) > 3 || (Number(version[0]) === 3 && Number(version[1]) >= 58);
      var options = {
        center: this.coords('data-center-lat', 'data-center-lng') || this.position,
        zoom: parseInt(this.getAttribute('data-zoom'), 10) || 15,
        // Cooperative: one finger scrolls the page, two move the map; Ctrl/Cmd + wheel zooms.
        gestureHandling: 'cooperative',
        scaleControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: ['roadmap', 'rx_locations'],
          style: gm.MapTypeControlStyle.HORIZONTAL_BAR,
          position: gm.ControlPosition.TOP_LEFT
        },
        fullscreenControl: true,
        fullscreenControlOptions: { position: gm.ControlPosition.TOP_RIGHT },
        streetViewControl: true,
        streetViewControlOptions: { position: gm.ControlPosition.RIGHT_BOTTOM }
      };
      if (hasCamera) {
        options.cameraControl = true;
        options.cameraControlOptions = { position: gm.ControlPosition.RIGHT_BOTTOM };
      } else {
        options.zoomControl = true;
        options.zoomControlOptions = { position: gm.ControlPosition.RIGHT_BOTTOM };
      }
      var map = new gm.Map(this.canvas, options);
      // "Locations" = the WP style as a map type, selected on load; "Map" = Google's roadmap.
      map.mapTypes.set('rx_locations', new gm.StyledMapType(MAP_STYLES, { name: 'Locations' }));
      map.setMapTypeId('rx_locations');
      new google.maps.Marker({
        position: this.position,
        map: map,
        title: this.getAttribute('data-title') || '',
        icon: this.getAttribute('data-marker') || undefined
      });
    }

    // No key / refused key / script error: hide the map box, the contact card
    // takes the no-map layout (no gray error area).
    fail() {
      this.failed = true;
      if (this.observer) this.observer.disconnect();
      this.hidden = true;
      var wrap = this.closest('.rx-contact__wrap--map');
      if (wrap) wrap.classList.remove('rx-contact__wrap--map');
    }
  }

  /* ------------------------------------------------------------------------
     <rx-contact-form>
     ------------------------------------------------------------------------ */
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Adobe Launch loads after consent (rx-tracking.js), so wait for it.
  function trackWhenReady(name) {
    if (!document.getElementById('rx-tracking-config')) return;
    var tries = 0;
    (function attempt() {
      var satellite = window._satellite;
      if (satellite && typeof satellite.track === 'function') {
        try { satellite.track(name); } catch (e) { /* ignore */ }
        return;
      }
      tries += 1;
      if (tries < 40) setTimeout(attempt, 500);
    })();
  }

  var RELAY_TIMEOUT = 15000;
  var RELAY_MIN_FILL = 3000; // the relay drops faster submits as bots
  // Relay accepts Mr. / Miss / Mrs. / Ms. / Mx.; the form's option values are WP's.
  var RELAY_TITLES = { Mrs: 'Mrs.', Ms: 'Ms.', Mx: 'Mx.' };

  class RxContactForm extends HTMLElement {
    connectedCallback() {
      var success = this.querySelector('[data-rx-contact-success]');
      if (success) {
        this.showSuccess(success);
        // A reload of the confirmation must not count as a second submit.
        if (window.history && window.history.replaceState && /[?&]contact_posted=/.test(window.location.search)) {
          var url = new URL(window.location.href);
          url.searchParams.delete('contact_posted');
          window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        }
        return;
      }

      this.form = this.querySelector('form');
      if (!this.form) return;
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-rx-step]'));
      this.requiredMessage = this.dataset.requiredMessage || 'This field is required.';
      this.emailMessage = this.dataset.emailMessage || this.requiredMessage;
      this.form.noValidate = true;
      this.emailInput = this.form.querySelector('[data-rx-relay="email"]');
      this.phoneInput = this.form.querySelector('[data-rx-relay="phone"]');

      this.addEventListener('click', this.onClick.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('input', this.onInput.bind(this));
      this.form.addEventListener('change', this.onInput.bind(this));
      this.initFloatingLabels();
      this.initRelay();
    }

    // Confirmation (after Shopify's contact_posted reload or a relay 200): breadcrumb + title on
    // the contact form page, focus, and the Rolex contactForm event.
    showSuccess(success) {
      if (success.querySelector('[data-rx-contact-page-success]')) {
        var breadcrumb = document.querySelector('.rx-breadcrumbs__text[aria-current="page"]');
        if (breadcrumb) breadcrumb.textContent = 'Thank You';
        document.title = 'Thank You | ' + document.title.split(' | ').pop();
      }
      success.focus({ preventScroll: true });
      trackWhenReady('contactForm');
    }

    /* ---- Form relay ------------------------------------------------------- */

    initRelay() {
      this.relayUrl = this.dataset.relayUrl || '';
      if (!this.relayUrl) return;
      this.relayTs = Date.now();
      var hp = document.createElement('input');
      hp.type = 'text';
      hp.name = '_hp';
      hp.tabIndex = -1;
      hp.autocomplete = 'off';
      hp.setAttribute('aria-hidden', 'true');
      hp.style.cssText = 'position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden;';
      this.form.appendChild(hp);
      this.hpInput = hp;
      this.form.addEventListener('submit', this.onRelaySubmit.bind(this), true);
    }

    // Email or phone, with the message on the email field.
    checkEmailOrPhone() {
      if (!this.emailInput || !this.phoneInput) return true;
      if (this.emailInput.value.trim() || this.phoneInput.value.trim()) {
        this.emailOrPhoneError = false;
        return this.check(this.emailInput) && this.clearFieldError(this.phoneInput);
      }
      this.setFieldError(this.emailInput, this.dataset.emailOrPhoneMessage || this.requiredMessage);
      this.emailOrPhoneError = true;
      this.emailInput.focus();
      return false;
    }

    relayPayload() {
      var data = { form_id: 'rolex-contact' };
      Array.prototype.forEach.call(this.form.querySelectorAll('[data-rx-relay]'), function (el) {
        var name = el.getAttribute('data-rx-relay');
        var value = el.type === 'checkbox' ? el.checked : el.value.trim();
        if (name === 'title' && RELAY_TITLES[value]) value = RELAY_TITLES[value];
        data[name] = value;
      });
      data.page_url = window.location.origin + window.location.pathname;
      if (!data.product_url) data.product_url = data.page_url;
      data._hp = this.hpInput ? this.hpInput.value : '';
      data._ts = this.relayTs;
      return data;
    }

    setPending(on) {
      this.form.setAttribute('aria-busy', on ? 'true' : 'false');
      Array.prototype.forEach.call(this.form.querySelectorAll('[type="submit"]'), function (button) {
        button.disabled = on;
      });
    }

    relayErrors(message) {
      var box = this.form.querySelector('[data-rx-relay-errors]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }

    sendToRelay() {
      var self = this;
      var data = this.relayPayload();
      var early = RELAY_MIN_FILL - (Date.now() - data._ts);
      this.pending = true;
      this.setPending(true);
      this.relayErrors('');

      new Promise(function (resolve) { setTimeout(resolve, early > 0 ? early : 0); })
        .then(function () {
          var controller = typeof AbortController === 'function' ? new AbortController() : null;
          var timer = setTimeout(function () { if (controller) controller.abort(); }, RELAY_TIMEOUT);
          return fetch(self.relayUrl, {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(data),
            signal: controller ? controller.signal : undefined
          }).then(function (response) {
            clearTimeout(timer);
            return response.json().catch(function () { return {}; }).then(function (json) {
              return { status: response.status, ok: response.ok && json.ok, json: json };
            });
          }, function () {
            clearTimeout(timer);
            return { status: 0 };
          });
        })
        .then(function (result) {
          self.pending = false;
          if (result.ok) return self.relaySuccess();
          if (!result.status || result.status >= 500) return self.relayFallback();
          self.setPending(false);
          if (result.status === 422 && result.json.fields) return self.relayFieldErrors(result.json.fields);
          self.relayErrors(result.json.message || self.dataset.relayErrorMessage || self.requiredMessage);
        });
    }

    relaySuccess() {
      var template = this.form.querySelector('template[data-rx-relay-success]');
      try { this.form.reset(); } catch (e) { /* ignore */ }
      if (!template) return;
      this.form.replaceChildren(template.content.cloneNode(true));
      var success = this.form.querySelector('[data-rx-contact-success]');
      if (!success) return;
      var rect = this.getBoundingClientRect();
      if (rect.top < 0) this.scrollIntoView({ block: 'start' });
      this.showSuccess(success);
    }

    // Relay unreachable / timed out / 5xx: Shopify's contact form, as before (form.submit() is
    // Shopify's hCaptcha-guarded submit when that script is on the page). Shopify needs an email
    // address, so a phone-only message gets the error message (try again / call) instead.
    relayFallback() {
      this.setPending(false);
      if (this.emailInput && !this.emailInput.value.trim()) {
        this.relayErrors(this.dataset.relayFallbackMessage || this.dataset.relayErrorMessage || this.requiredMessage);
        return;
      }
      this.form.submit();
    }

    relayFieldErrors(fields) {
      var first = null;
      Object.keys(fields).forEach(function (name) {
        var input = this.form.querySelector('[data-rx-relay="' + name + '"]');
        if (!input) return;
        this.setFieldError(input, fields[name]);
        if (!first) first = input;
      }, this);
      this.relayErrors(this.dataset.relayErrorMessage || '');
      if (!first) return;
      var stepIndex = this.steps.findIndex(function (step) { return step.contains(first); });
      if (stepIndex > -1 && this.steps[stepIndex].hidden) this.show(stepIndex);
      first.focus();
    }

    errorFor(input) {
      var field = input.closest('.rx-contact__field');
      if (!field) return null;
      var error = field.querySelector('.rx-contact__error');
      if (!error) {
        error = document.createElement('p');
        error.className = 'rx-contact__error';
        error.id = (input.id || 'rx-field') + '-error';
        error.hidden = true;
        field.appendChild(error);
      }
      return error;
    }

    setFieldError(input, message) {
      var field = input.closest('.rx-contact__field');
      var error = this.errorFor(input);
      if (field) field.classList.add('rx-contact__field--error');
      input.setAttribute('aria-invalid', 'true');
      if (error) {
        error.textContent = message;
        error.hidden = false;
        input.setAttribute('aria-describedby', error.id);
      }
    }

    clearFieldError(input) {
      var field = input.closest('.rx-contact__field');
      var error = field && field.querySelector('.rx-contact__error');
      if (field) field.classList.remove('rx-contact__field--error');
      input.removeAttribute('aria-invalid');
      if (error) {
        error.textContent = '';
        error.hidden = true;
        input.removeAttribute('aria-describedby');
      }
      return true;
    }

    // Floating labels: inputs / textarea float by CSS (:placeholder-shown, :focus-within);
    // .rx-is-filled covers what CSS can't see: a select's value, browser autofill (Chrome
    // doesn't expose the value before a user gesture: the rx-autofill-start animation fires
    // instead) and values restored on back / forward navigation.
    initFloatingLabels() {
      var sync = this.syncFilled.bind(this);
      var syncAll = function () {
        Array.prototype.forEach.call(this.form.querySelectorAll('.rx-contact__field--float input, .rx-contact__field--float select, .rx-contact__field--float textarea'), sync);
      }.bind(this);
      this.form.addEventListener('input', function (event) { sync(event.target); });
      this.form.addEventListener('change', function (event) { sync(event.target); });
      this.form.addEventListener('animationstart', function (event) {
        if (event.animationName === 'rx-autofill-start') sync(event.target, true);
        else if (event.animationName === 'rx-autofill-end') sync(event.target);
      });
      window.addEventListener('pageshow', syncAll);
      syncAll();
    }

    syncFilled(el, autofilled) {
      var field = el && el.closest && el.closest('.rx-contact__field--float');
      if (!field || !this.contains(field)) return;
      field.classList.toggle('rx-is-filled', !!autofilled || el.value !== '');
    }

    onClick(event) {
      if (event.target.closest('[data-rx-step-next]')) {
        if (this.validate(this.steps[0])) this.show(1);
      } else if (event.target.closest('[data-rx-step-prev]')) {
        this.show(0);
      }
    }

    onSubmit(event) {
      var step = this.steps[1];
      if (!this.validate(this.steps[0])) {
        event.preventDefault();
        this.show(0);
        this.validate(this.steps[0]);
      } else if (step && !this.validate(step)) {
        event.preventDefault();
      } else if (!this.checkEmailOrPhone()) {
        event.preventDefault();
      }
    }

    // Relay on. Capture phase on the form: runs before Shopify's contact-form hCaptcha listener,
    // which always posts the form itself; the event stops here so the message isn't sent twice.
    onRelaySubmit(event) {
      if (event.target !== this.form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.pending) return;
      if (!this.validate(this.steps[0])) {
        this.show(0);
        this.validate(this.steps[0]);
        return;
      }
      if (this.steps[1] && !this.validate(this.steps[1])) return;
      if (!this.checkEmailOrPhone()) return;
      this.sendToRelay();
    }

    onInput(event) {
      // Typing in Email or Phone Number clears the "email or phone" message.
      if (this.emailOrPhoneError && (event.target === this.emailInput || event.target === this.phoneInput)) {
        this.emailOrPhoneError = false;
        this.clearFieldError(this.emailInput);
        return;
      }
      var field = event.target.closest('.rx-contact__field');
      if (!field || !field.classList.contains('rx-contact__field--error')) return;
      // A relay error on a field the form doesn't check itself (phone, title, …): clear it on edit.
      if (event.target.hasAttribute('data-rx-required') || event.target.type === 'email') this.check(event.target);
      else this.clearFieldError(event.target);
    }

    show(index) {
      this.steps.forEach(function (step, i) {
        step.hidden = i !== index;
      });
      var heading = this.steps[index] && this.steps[index].querySelector('[data-rx-step-heading]');
      var rect = this.getBoundingClientRect();
      if (rect.top < 0) this.scrollIntoView({ block: 'start' });
      if (heading) heading.focus({ preventScroll: true });
    }

    // Returns true when the field is valid; shows or clears its message.
    check(input) {
      var value = input.type === 'checkbox' ? (input.checked ? 'on' : '') : input.value.trim();
      var message = '';
      if (input.hasAttribute('data-rx-required') && !value) message = this.requiredMessage;
      else if (input.type === 'email' && value && !EMAIL.test(value)) message = this.emailMessage;

      var field = input.closest('.rx-contact__field');
      var error = field && field.querySelector('.rx-contact__error');
      if (field) field.classList.toggle('rx-contact__field--error', !!message);
      if (message) {
        input.setAttribute('aria-invalid', 'true');
        if (error) {
          error.textContent = message;
          error.hidden = false;
          input.setAttribute('aria-describedby', error.id);
        }
      } else {
        input.removeAttribute('aria-invalid');
        if (error) {
          error.textContent = '';
          error.hidden = true;
          input.removeAttribute('aria-describedby');
        }
      }
      return !message;
    }

    validate(container) {
      if (!container) return true;
      var first = null;
      var inputs = container.querySelectorAll('[data-rx-required], input[type="email"]');
      Array.prototype.forEach.call(inputs, function (input) {
        if (!this.check(input) && !first) first = input;
      }, this);
      if (first) first.focus();
      return !first;
    }
  }

  if (!customElements.get('rx-store-hours')) customElements.define('rx-store-hours', RxStoreHours);
  if (!customElements.get('rx-map')) customElements.define('rx-map', RxMap);
  if (!customElements.get('rx-contact-form')) customElements.define('rx-contact-form', RxContactForm);
})();
